import { animate, inView, stagger } from '@theme/motion';

const REVEAL_SELECTOR = '[data-reveal]';
const EASE_OUT_EXPO = [0.22, 1, 0.36, 1];

function getBlocks(wrapper) {
  return Array.from(wrapper.children).filter((el) => el.nodeType === 1);
}

function reveal(wrapper) {
  if (wrapper.dataset.revealing === 'true') return;
  wrapper.dataset.revealing = 'true';

  const blocks = getBlocks(wrapper);
  if (!blocks.length) {
    wrapper.dataset.revealed = 'true';
    return;
  }

  const staggerStep = blocks.length > 1
    ? Math.min(0.12, 0.8 / (blocks.length - 1))
    : 0;

  const blocksAnim = animate(
    blocks,
    { opacity: [0, 1], transform: ['translateY(24px)', 'translateY(0px)'] },
    {
      duration: 0.7,
      delay: stagger(staggerStep, { start: 0.05 }),
      easing: EASE_OUT_EXPO,
    }
  );

  // Headings get an editorial "wipe up" reveal: the type is masked from the
  // bottom and unveils upward, layered over the block's rise above.
  const headline = wrapper.querySelector('h1, h2');
  if (headline && wrapper.dataset.revealHeadline !== 'false') {
    animate(
      headline,
      { clipPath: ['inset(100% 0 0 0)', 'inset(0% 0 0 0)'] },
      { duration: 1, delay: 0.05, easing: EASE_OUT_EXPO }
    );
  }

  const finalize = () => {
    wrapper.dataset.revealed = 'true';
  };

  if (blocksAnim && blocksAnim.finished) {
    blocksAnim.finished.then(finalize).catch(finalize);
  } else {
    setTimeout(finalize, 1500);
  }
}

function isInInitialViewport(el) {
  const rect = el.getBoundingClientRect();
  return rect.top < window.innerHeight && rect.bottom > 0;
}

function init() {
  const wrappers = document.querySelectorAll(REVEAL_SELECTOR);
  wrappers.forEach((wrapper) => {
    if (isInInitialViewport(wrapper)) {
      reveal(wrapper);
    } else {
      inView(wrapper, () => reveal(wrapper), { amount: 0.25 });
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// Section morphs (e.g. collection filter changes) strip the JS-set
// `data-revealed`/`data-revealing` attributes that gate
// `[data-reveal] > * { opacity: 0 }` in scripts.liquid, leaving newly
// rendered content invisible. Re-run the reveal whenever they disappear.
new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    if (mutation.type !== 'attributes') continue;
    const target = mutation.target;
    if (!(target instanceof HTMLElement)) continue;
    if (!target.hasAttribute('data-reveal')) continue;
    if (target.dataset.revealed === 'true') continue;
    if (target.dataset.revealing === 'true') continue;
    reveal(target);
  }
}).observe(document.documentElement, {
  subtree: true,
  attributes: true,
  attributeFilter: ['data-revealed', 'data-revealing'],
});
